<template>
  <div :class="$style.container">
    <ItemTable
      v-if="browserPatchPoliciesExist"
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :disableBoundCount="disableBoundCount"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedItem"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleLockedItems"
      :hasSelectColumn="true"
      :itemType="itemType"
      :mainActionMenuItems="mainActionMenuItems"
      :onMountedAfterFetch="onMountedAfterFetch"
      :parentOS="parentOS"
      :reportType="reportType"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="selectionText"
      :setSelectedModelId="setSelectedModelId"
      :showActionBar="true"
      :showBound="false"
      :showBulkDelete="true"
      :showMainAction="true"
      :showSearchForm="true"
      :useDropDownFilterComponent="useDropDownFilterComponent"
      :viewport="viewport"
    />
    <GettingStartedPanel
      :buttonLabel="zeroStateBtnText"
      :iconNameStyle="gettingStartedIconStyle"
      iconName="premiumDiscover"
      :itemType="browserPatchManagement"
      :visible="!browserPatchPoliciesExist"
      @ctaButtonClick="loadDefaultPolicies"
    />
    <LoadingOverlay
      :showLoadingOverlay="setLoadingOverlay"
    />
  </div>
</template>

<script>
import { ReportTypes } from 'client/Reports/ReportMetadata';
import {
  browserPolicyNameToBrowser,
} from 'client/util/Constants/OsPatchMgmtConstants';
import { clone } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import AsidePanelChildComponentMethodsMixin
  from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin
  from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import Constants, {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import DeleteConfirmationModal from 'components/Modal/ModalComponents/DeleteConfirmationModal.vue';
import GettingStartedPanel from 'client/components/GettingStartedPanel/GettingStartedPanel.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin
  from 'components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ItemType from 'client/util/Constants/ItemType';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import PoliciesMixin from 'client/OsManagement/mixins/PoliciesMixin';
import PolicyOSIcon from 'client/components/ItemTable/BodyComponents/PolicyOSIcon.vue';
import UniversalIcon from 'public/img/icons/universal-logo.svg?inline';
import Util from 'client/util/Util';
import pluralize from 'pluralize';

const { ConfigurationItemType } = ItemType;
const configurationItem = new ConfigurationItemType();

const { browserPatchManagement } = ItemTypeConsts;
const { error, success } = Constants;
const { columnTypes } = ItemTableConsts;
const storeModule = StoreModuleMap[ItemTypeConsts.browserPatchManagement];
const policyTemplateCollection = StoreModuleMap[ItemTypeConsts.policyTemplate];
const storeModel = StoreModelMap[ItemTypeConsts.configuration];
const configurationGroupModel = StoreModelMap[ItemTypeConsts.configurationGroup];

export default {
  name: 'BrowserPatchManagementItemTable',

  components: {
    GettingStartedPanel,
    ItemTable,
    LoadingOverlay,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    PoliciesMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTablePropsMixin,
    ItemTableMethodsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.browserPatchManagement,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
  },

  data: () => ({
    ItemTypeConsts,
    browserPatchManagement,
    browserPatchPolicies: {
      chrome: {},
      totalCount: 0,
    },
    policiesConfigFields: {},
    reportType: ReportTypes.browserPatchMgmtPolicy,
    setLoadingOverlay: false,
    storeModule,
    zeroStateBtnText: 'Load Default Policies',
  }),

  computed: {
    ...mapGetters('PlanModule', [
      'planHasFeature',
    ]),

    ...mapGetters(storeModule, {
      selectedItems: 'getSelectedItems',
    }),

    ...mapGetters(storeModel, [
      'item',
    ]),

    ...mapGetters(policyTemplateCollection, {
      policyTemplates: 'getCollection',
    }),

    browserPatchPoliciesExist() {
      return this.browserPatchPolicies.totalCount > 0;
    },

    columns() {
      return [
        {
          bodyComponent: PolicyOSIcon,
          classNames: {
            body: [columnTypes.iconColumn],
          },
          headingText: 'Type',
          sortBy: 'template.osMetaFamily',
        },
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'name',
            subtitleField: 'template.displayName',
          },
          sortBy: 'name',
          headingText: 'Name',
        },
        {
          bodyComponent: 'RequirementsDetail',
          headingText: 'Requirements',
        },
      ];
    },

    gettingStartedIconStyle() {
      const iconStyle = {
        'align-items': 'center',
        'background-color': 'var(--jcTertiary50)',
        'border-radius': '0.313rem',
        display: 'inline-block',
        height: '2rem',
        'justify-content': 'center',
        'margin-right': '0.75rem',
        width: '2rem',
        'font-size': '1.07rem',
        border: '1px solid var(--jcTertiary50)',
        'padding-top': '0.4rem',
      };

      const iconHideStyle = {
        display: 'none',
      };
      return this.planHasFeature(trialFeatures.osPatchManagement.featureCode)
        ? iconHideStyle
        : iconStyle;
    },

    mainActionMenuItems() {
      const filteredNames = clone(this.policyTemplates);
      return filteredNames.map(template => {
        const templateName = template.name.split('_')[0]; // Get the policy name
        const metadata = {
          chrome: {
            name: this.browserPatchPolicies.chrome.length
              ? 'Google Chrome'
              : 'Load Default Google Chrome Policy',
            icon: UniversalIcon,
          },
        }[templateName] || {
          name: template.name,
          icon: UniversalIcon,
        };
        return {
          clickHandler: this.browserPatchPolicies[templateName].length
            ? () => this.redirectToAside(template.osMetaFamily, template.id)
            : () => this.loadDefaultPolicies(template.name),
          component: metadata.icon,
          text: metadata.name,
        };
      });
    },

    route() {
      return ItemTypeRoutes[this.browserPatchManagement];
    },

    selectionText() {
      return {
        label: this.inMainPanel ? 'selected' : 'bound',
        plural: configurationItem.labelPlural,
        singular: configurationItem.label,
      };
    },
  },

  beforeCreate() {
    this.$options.methods.handleBeforeCreate(this.$options, storeModule);
  },

  async created() {
    this.handleCreate();
  },

  methods: {
    ...mapActions(storeModule, [
      'deleteItems',
      'fetchCollection',
    ]),

    ...mapActions(storeModel, [
      'fetchItem',
      'saveItem',
    ]),

    ...mapActions(configurationGroupModel, [
      'bulkCreateDefaultPolicyGroups',
    ]),

    async deleteSelectedPolicies() {
      ModalEventBus.close();
      const deleteResult = await this.deleteItems({
        ids: this.selectedItems,
        isOsPatchPolicy: true,
      });

      if (deleteResult.deletedIds.length > 0) {
        notify(success, `${deleteResult.deletedIds.length} ${pluralize('Policy', deleteResult.deletedIds.length)} successfully deleted.`);
        this.getBrowserPatchPoliciesCount();
      } else {
        notify(error, `There was an error deleting the selected ${pluralize('Policy', this.selectedItems.length)}`);
      }
    },

    async getBrowserPatchPoliciesCount() {
      const policies = await this.fetchBrowserPatchPolicies();
      Object.values(browserPolicyNameToBrowser).forEach(browser => {
        this.browserPatchPolicies[browser] = policies.results.filter(
          ({ template }) => template.name.includes(browser),
        );
      });
      this.browserPatchPolicies.totalCount = policies.totalCount;
    },

    async getPoliciesConfigFields() {
      this.policiesConfigFields = await this.getConfigFieldsBrowserPatch(['chrome_universal']);
      await this.getBrowserPatchPoliciesCount();
    },

    handleBulkDelete() {
      ModalEventBus.open({
        component: DeleteConfirmationModal,
        componentProps: {
          handleContinue: this.deleteSelectedPolicies,
          enableMainAction: true,
          bodyText: `Are you sure you would like to delete the selected ${pluralize('policy', this.selectedItems.length)}?`,
          headerText: `Delete ${pluralize('Policy', this.selectedItems.length)}`,
        },
      });
    },

    async handleCreate() {
      await this.getPoliciesConfigFields();
      this.$emit('loaded');
    },

    handleMainAction() {
      const browserPatchMgmtRouteName = 'configurationsMainPanelBrowserPatchMgmt';
      if (browserPatchMgmtRouteName !== this.$route.name) {
        this.$router.push({ name: browserPatchMgmtRouteName });
      }
    },

    handleRowClick(id) {
      Util.redirect(`#/configurations/patch/browser/${id}/details/`);
    },

    async loadDefaultPolicies(browser = '') {
      this.setLoadingOverlay = true;
      const templates = browser !== ''
        ? this.policyTemplates.filter(({ osMetaFamily }) => osMetaFamily === browser)
        : this.policyTemplates;

      await this.createDefaultPoliciesBrowserPatch(
        this.policiesConfigFields, templates,
      );
      await this.getBrowserPatchPoliciesCount();

      this.setLoadingOverlay = false;
    },

    onMountedAfterFetch() {
      this.$emit('loaded');
    },

    redirectToAside(policy, id) {
      Util.redirect(`#/configurations/configure/${policy}/${id}`);
    },
  },
};
</script>
<style module>
.container {
  background: var(--jcWhite300);
  position: relative;
}
</style>
